import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, money, fmtDate } from '../api'
import { Page } from '../App'
import { Card, Stat, Badge, Alert, Spinner, Empty } from '../components/ui'

interface DashboardData {
  today: string
  fy: string
  nextNumber: string | null
  seriesWarning: string | null
  outstanding: { total: number; overdue: number; count: number }
  aging: { not_due: number; d0_30: number; d31_60: number; d61_90: number; d90_plus: number }
  revenue: { mtd: number; fytd: number; fy_tax: number }
  counts: { drafts: number; overdue: number; paid: number; awaiting: number }
  expenses: { mtd: number; fytd: number; fytd_taxable: number; unpaid: number; tds_withheld: number; itc_fytd: number; itc_blocked: number }
  expenseByCategory: Array<{ category: string | null; spent: number; count: number }>
  profit: { fytd: number; mtd: number; revenueFytd: number; expenseFytd: number }
  gstPosition: { outputFy: number; itcFy: number; netPayableFy: number; outputMtd: number; itcMtd: number; netPayableMtd: number; itcBlocked: number }
  topClients: Array<{ id: string; legal_name: string; display_name: string | null; billed: number; outstanding: number }>
  recentActivity: Array<{
    id: string; number: string | null; status: string; total: number; currency: string
    issue_date: string; client_name: string; client_display_name: string | null
  }>
  upcomingRecurring: Array<{ id: string; name: string; next_run_on: string; mode: string; client_name: string }>
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    api
      .get<DashboardData>('/reports/dashboard')
      .then(setData)
      .catch((e) => setError(e.message))
  }, [])

  if (error) return <Page title="Dashboard"><Alert kind="error">{error}</Alert></Page>
  if (!data) return <Page title="Dashboard"><Spinner /></Page>

  const agingRows = [
    { label: 'Not yet due', value: data.aging.not_due },
    { label: '1–30 days', value: data.aging.d0_30 },
    { label: '31–60 days', value: data.aging.d31_60 },
    { label: '61–90 days', value: data.aging.d61_90 },
    { label: 'Over 90 days', value: data.aging.d90_plus },
  ]

  return (
    <Page
      title="Dashboard"
      subtitle={`FY ${data.fy} · next invoice will be ${data.nextNumber ?? '—'}`}
      actions={
        <button className="btn primary" onClick={() => navigate('/invoices/new')}>
          + New invoice
        </button>
      }
    >
      {data.seriesWarning && <Alert kind="warn">{data.seriesWarning}</Alert>}

      <div className="grid c4" style={{ marginBottom: 16 }}>
        <Stat
          label="Outstanding"
          value={money(data.outstanding.total)}
          note={`${data.outstanding.count} open invoice${data.outstanding.count === 1 ? '' : 's'}`}
        />
        <Stat
          label="Overdue"
          value={money(data.outstanding.overdue)}
          note={`${data.counts.overdue} invoice${data.counts.overdue === 1 ? '' : 's'} past due`}
          tone={data.outstanding.overdue > 0 ? 'danger' : undefined}
        />
        <Stat label="Revenue this month" value={money(data.revenue.mtd)} note="Taxable value" />
        <Stat label={`Revenue FY ${data.fy}`} value={money(data.revenue.fytd)} note={`GST collected ${money(data.revenue.fy_tax)}`} />
      </div>

      <div className="grid c2" style={{ marginBottom: 16 }}>
        <Card
          title={`Profit · FY ${data.fy}`}
          actions={<Link to="/reports" className="btn sm">Full P&L</Link>}
        >
          <table className="tbl">
            <tbody>
              <tr>
                <td>Revenue</td>
                <td className="num">{money(data.profit.revenueFytd)}</td>
              </tr>
              <tr>
                <td>Expenses</td>
                <td className="num">− {money(data.profit.expenseFytd)}</td>
              </tr>
              <tr style={{ background: '#fafbfc' }}>
                <td>
                  <strong>Net profit</strong>
                </td>
                <td className="num">
                  <strong style={{ color: data.profit.fytd >= 0 ? 'var(--ok)' : 'var(--danger)', fontSize: 15 }}>
                    {money(data.profit.fytd)}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="hint" style={{ marginTop: 8 }}>
            Both sides exclude GST — it passes through, so counting it would overstate both.
          </div>
        </Card>

        <Card title={`GST position · FY ${data.fy}`}>
          <table className="tbl">
            <tbody>
              <tr>
                <td>Output tax collected</td>
                <td className="num">{money(data.gstPosition.outputFy)}</td>
              </tr>
              <tr>
                <td>Input tax credit</td>
                <td className="num" style={{ color: 'var(--ok)' }}>− {money(data.gstPosition.itcFy)}</td>
              </tr>
              <tr style={{ background: '#fff6ed' }}>
                <td>
                  <strong>Net GST payable</strong>
                </td>
                <td className="num">
                  <strong style={{ fontSize: 15 }}>{money(data.gstPosition.netPayableFy)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="hint" style={{ marginTop: 8 }}>
            This month: {money(data.gstPosition.netPayableMtd)} payable.
            {data.gstPosition.itcBlocked > 0 && (
              <> {money(data.gstPosition.itcBlocked)} of credit is blocked under s.17(5).</>
            )}
          </div>
        </Card>
      </div>

      <div className="grid c2" style={{ marginBottom: 16 }}>
        <Card title="Ageing of receivables">
          <table className="tbl">
            <tbody>
              {agingRows.map((r) => (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  <td className="num" style={{ fontWeight: 600 }}>
                    {money(r.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12 }}>
            <Link to="/reports" className="btn sm">
              Full ageing report
            </Link>
          </div>
        </Card>

        <Card
          title={`Where the money goes · FY ${data.fy}`}
          actions={<Link to="/expenses" className="btn sm">All expenses</Link>}
        >
          {data.expenseByCategory.length === 0 ? (
            <Empty title="No expenses recorded yet" hint="Add rent, salaries, software or hosting to see profit and ITC." />
          ) : (
            <table className="tbl">
              <tbody>
                {data.expenseByCategory.map((c, i) => (
                  <tr key={i}>
                    <td>{c.category ?? 'Uncategorised'}</td>
                    <td className="num">{money(c.spent)}</td>
                    <td className="num muted small">{c.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data.expenses.unpaid > 0 && (
            <div className="hint" style={{ marginTop: 8, color: 'var(--danger)' }}>
              {money(data.expenses.unpaid)} still unpaid.
            </div>
          )}
        </Card>
      </div>

      <div className="grid c2" style={{ marginBottom: 16 }}>
        <Card title={`Top clients · FY ${data.fy}`}>
          {data.topClients.length === 0 ? (
            <Empty title="No billing yet this financial year" />
          ) : (
            <table className="tbl">
              <tbody>
                {data.topClients.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => navigate(`/clients/${c.id}`)}>
                    <td>{c.display_name || c.legal_name}</td>
                    <td className="num">{money(c.billed)}</td>
                    <td className="num muted small">{c.outstanding > 0 ? `${money(c.outstanding)} due` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div className="grid c2">
        <Card
          title="Recent activity"
          actions={
            <Link to="/invoices" className="btn sm">
              All invoices
            </Link>
          }
          tight
        >
          {data.recentActivity.length === 0 ? (
            <Empty icon="▤" title="No invoices yet" hint="Create your first invoice to get started." />
          ) : (
            <table className="tbl">
              <tbody>
                {data.recentActivity.map((i) => (
                  <tr key={i.id} className="clickable" onClick={() => navigate(`/invoices/${i.id}`)}>
                    <td className="mono nowrap">{i.number ?? 'Draft'}</td>
                    <td>{i.client_display_name || i.client_name}</td>
                    <td className="num">{money(i.total, i.currency)}</td>
                    <td className="right">
                      <Badge status={i.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card
          title="Upcoming recurring"
          actions={
            <Link to="/recurring" className="btn sm">
              Manage
            </Link>
          }
          tight
        >
          {data.upcomingRecurring.length === 0 ? (
            <Empty icon="↻" title="No recurring invoices scheduled" hint="Set one up for retainer clients." />
          ) : (
            <table className="tbl">
              <tbody>
                {data.upcomingRecurring.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div className="muted small">{r.client_name}</div>
                    </td>
                    <td className="num nowrap">{fmtDate(r.next_run_on)}</td>
                    <td className="right">
                      <Badge status={r.mode === 'auto_send' ? 'paid' : 'draft'}>
                        {r.mode === 'auto_send' ? 'Auto-send' : 'Draft only'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </Page>
  )
}
